import { useState, useEffect, useRef, useMemo, type ReactNode } from 'react';
import { Zap, CheckCircle, AlertTriangle, Loader2, Wifi, DollarSign } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { useOperatorLocationPicker } from '../../hooks/useOperatorLocationPicker';
import { SearchableSelect } from '../molecules/SearchableSelect';
import type { SearchableSelectOption } from '../molecules/SearchableSelect';
import type { ThirdPartyManufacturer, CreateThirdPartyMachineRequest, MachineGroupInfo } from '../../types/machine-setup-types';

interface ThirdPartyStepProps {
  onSubmit: (req: CreateThirdPartyMachineRequest) => Promise<{ isSuccess: boolean; machineId: number | null; message: string | null }>;
  onLoadManufacturers: () => Promise<ThirdPartyManufacturer[]>;
  onLoadMachineGroups: (actorId: number) => Promise<MachineGroupInfo[]>;
}

const CUSTOM_MANUFACTURER_ID = -1;

const FEATURE_BADGES: Array<{ key: keyof ThirdPartyManufacturer; label: string; icon: ReactNode }> = [
  { key: 'isDc',         label: 'DC Fast Charging',    icon: <Zap className="h-3.5 w-3.5" /> },
  { key: 'isOcppTariff', label: 'OCPP Tariff Pricing',  icon: <DollarSign className="h-3.5 w-3.5" /> },
  { key: 'isOcppCost',   label: 'Running Cost Display', icon: <Wifi className="h-3.5 w-3.5" /> },
];

export function ThirdPartyStep({ onSubmit, onLoadManufacturers, onLoadMachineGroups }: ThirdPartyStepProps) {
  const [manufacturers, setManufacturers] = useState<ThirdPartyManufacturer[]>([]);
  const [loadingMfrs, setLoadingMfrs] = useState(true);
  const [mfrError, setMfrError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [selectedMfrId, setSelectedMfrId] = useState<number | null>(null);
  const [machineName, setMachineName] = useState('');
  const [chargerSerialNumber, setChargerSerialNumber] = useState('');
  const [machineGroupId, setMachineGroupId] = useState<number | null>(null);
  const [machineGroups, setMachineGroups] = useState<MachineGroupInfo[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [groupsRetryKey, setGroupsRetryKey] = useState(0);

  // ── Operator & Location picker (shared hook) ────────────────────────────────
  const {
    operators, selectedOperator, setSelectedOperator,
    locations, setLocations, locationsLoading, selectedLocation, setSelectedLocation,
    setOperatorQuery, setLocationQuery,
  } = useOperatorLocationPicker();

  const operatorOptions: SearchableSelectOption[] = useMemo(
    () => operators.map((o) => ({ id: String(o.operatorId), label: o.name, subLabel: `ID: ${o.operatorId}` })),
    [operators],
  );

  const locationOptions: SearchableSelectOption[] = useMemo(
    () => locations.map((l) => ({ id: String(l.actorId), label: l.name, subLabel: `ID: ${l.actorId}` })),
    [locations],
  );

  // resolved actor ID (operator or location) — used for machine creation placement
  const [resolvedActorId, setResolvedActorId] = useState<number>(0);
  // resolved operator ID — always the operator level, used for machine group lookups
  const [resolvedOperatorId, setResolvedOperatorId] = useState<number>(0);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ isSuccess: boolean; machineId: number | null; message: string | null } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);

  // Scroll to form fields whenever a manufacturer is selected
  useEffect(() => {
    if (selectedMfrId !== null) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [selectedMfrId]);

  // Load manufacturers on mount
  useEffect(() => {
    onLoadManufacturers()
      .then(setManufacturers)
      .catch(e => setMfrError(e instanceof Error ? e.message : 'Failed to load manufacturers'))
      .finally(() => setLoadingMfrs(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load machine groups when operator changes.
  // Groups live at operator level — use resolvedOperatorId, not resolvedActorId,
  // so selecting a sub-location doesn't wipe the group list.
  useEffect(() => {
    if (!resolvedOperatorId || resolvedOperatorId <= 0) {
      setMachineGroups([]);
      setMachineGroupId(null);
      return;
    }
    setLoadingGroups(true);
    setMachineGroupId(null);
    setGroupsError(null);
    onLoadMachineGroups(resolvedOperatorId)
      .then(setMachineGroups)
      .catch((err: unknown) => {
        setMachineGroups([]);
        setGroupsError(err instanceof Error ? err.message : 'Failed to load machine groups');
      })
      .finally(() => setLoadingGroups(false));
  }, [resolvedOperatorId, groupsRetryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query.trim()
    ? manufacturers.filter(m =>
        m.manufacturerName.toLowerCase().includes(query.toLowerCase()) ||
        (m.modelName ?? '').toLowerCase().includes(query.toLowerCase())
      )
    : manufacturers;

  const selectedMfr = selectedMfrId === CUSTOM_MANUFACTURER_ID
    ? null
    : manufacturers.find(m => m.id === selectedMfrId);
  const isCustom = selectedMfrId === CUSTOM_MANUFACTURER_ID;

  const canSubmit =
    selectedMfrId !== null &&
    machineName.trim() !== '' &&
    resolvedActorId > 0 &&
    chargerSerialNumber.trim() !== '';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await onSubmit({
        machineName: machineName.trim(),
        actorId: resolvedActorId,
        machineGroupId: machineGroupId ?? undefined,
        chargerIdNumber: chargerSerialNumber.trim(),
        manufacturerId: isCustom ? null : (selectedMfr?.id ?? null),
      });
      setResult(res);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

  // ── Result screen ─────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="py-6 text-center">
        {result.isSuccess ? (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Machine Created</h2>
            <p className="text-sm text-gray-500 mb-2">
              Machine ID: <span className="font-mono font-semibold text-gray-900">{result.machineId}</span>
            </p>
            {result.message && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                {result.message}
              </p>
            )}
          </>
        ) : (
          <>
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Creation Failed</h2>
            <p className="text-sm text-red-600">{result.message || 'Unknown error'}</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Manufacturer selection */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Select Manufacturer</h2>
        <p className="text-sm text-gray-500 mb-4">Choose the manufacturer of the third-party OCPP charger.</p>

        {loadingMfrs ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading manufacturers…
          </div>
        ) : mfrError ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{mfrError}</p>
        ) : (
          <>
            <input
              type="text"
              className={`${inp} mb-3`}
              placeholder="Search manufacturers…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(mfr => {
              const isSelected = selectedMfrId === mfr.id;
              return (
                <button
                  key={mfr.id}
                  onClick={() => setSelectedMfrId(mfr.id)}
                  className={[
                    'flex flex-col items-start text-left rounded-lg border-2 p-3 transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-primary/50',
                  ].join(' ')}
                >
                  <p className="font-medium text-gray-900 text-sm">{mfr.manufacturerName}</p>
                  {mfr.modelName && <p className="text-xs text-gray-500 mt-0.5">{mfr.modelName}</p>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {FEATURE_BADGES.filter(f => mfr[f.key as keyof ThirdPartyManufacturer] === true).map(f => (
                      <span
                        key={f.key}
                        className="inline-flex items-center gap-1 text-xs bg-primary text-gray-900 rounded-full px-2 py-0.5"
                      >
                        {f.icon}{f.label}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}

            {/* Custom / Unknown manufacturer */}
            <button
              onClick={() => setSelectedMfrId(CUSTOM_MANUFACTURER_ID)}
              className={[
                'flex flex-col items-start text-left rounded-lg border-2 p-3 transition-all',
                isCustom
                  ? 'border-amber-400 bg-amber-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-amber-300',
              ].join(' ')}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                <p className="font-medium text-gray-900 text-sm">Custom / Unknown</p>
              </div>
              <p className="text-xs text-gray-500">Manufacturer not in the list</p>
            </button>
          </div>
          </>
        )}

        {/* Custom manufacturer disclaimer */}
        {isCustom && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              We cannot guarantee full OCPP compliance for unknown manufacturers. Some features may not
              work correctly, including tariff pricing, running cost display, and remote start/stop.
            </span>
          </div>
        )}
      </div>

      {/* Form fields — only shown once a manufacturer is chosen */}
      {selectedMfrId !== null && (
        <div ref={formRef} className="space-y-4 border-t border-gray-100 pt-6">
          <h3 className="text-base font-semibold text-gray-900">Machine Details</h3>

          {/* Machine name */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Machine Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={inp}
              placeholder="e.g. Parking Lot A — Bay 1"
              value={machineName}
              onChange={e => setMachineName(e.target.value)}
            />
          </div>

          {/* Operator search */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Operator <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={operatorOptions}
              value={selectedOperator ? String(selectedOperator.operatorId) : undefined}
              onValueChange={(id) => {
                const op = operators.find((o) => o.operatorId === Number(id));
                if (!op) return;
                setSelectedOperator(op);
                setOperatorQuery(op.name);
                setResolvedActorId(op.operatorId);
                setResolvedOperatorId(op.operatorId);
              }}
              placeholder="Select an operator…"
              searchPlaceholder="Search by name or operator ID…"
              emptyMessage="No operators found"
              isLoading={operators.length === 0}
              loadingMessage="Loading operators…"
            />
          </div>

          {/* Location (shown once operator is selected) */}
          {selectedOperator && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Location{' '}
                <span className="text-gray-400">
                  {locationsLoading ? '(loading…)' : locations.length === 0 ? '(no sub-locations)' : ''}
                </span>
              </label>
              {locationsLoading ? (
                <div className="flex items-center gap-2 h-10 text-sm text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                  Loading locations…
                </div>
              ) : locations.length === 0 ? (
                <p className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
                  No sub-locations found. The machine will be assigned directly to <span className="font-medium">{selectedOperator.name}</span>.
                </p>
              ) : (
                <SearchableSelect
                  options={locationOptions}
                  value={selectedLocation ? String(selectedLocation.actorId) : undefined}
                  onValueChange={(id) => {
                    const loc = locations.find((l) => l.actorId === Number(id));
                    if (!loc) return;
                    setSelectedLocation(loc);
                    setLocationQuery(loc.name);
                    setResolvedActorId(loc.actorId); // machine placed under location
                    // resolvedOperatorId stays unchanged — groups still loaded from operator
                  }}
                  placeholder="Select a location…"
                  searchPlaceholder="Search by location name or ID…"
                  emptyMessage="No locations found"
                />
              )}
            </div>
          )}

          {/* Charger Serial Number + Machine Group */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Charger Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inp}
                placeholder="e.g. CHARGER-001"
                value={chargerSerialNumber}
                onChange={e => setChargerSerialNumber(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Machine Group</label>
              {loadingGroups ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading groups…
                </div>
              ) : groupsError ? (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-red-500">{groupsError}</p>
                  <button
                    type="button"
                    className="text-xs text-primary underline hover:text-primary/80"
                    onClick={() => setGroupsRetryKey((k) => k + 1)}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <select
                  className={inp}
                  value={machineGroupId ?? ''}
                  onChange={e => setMachineGroupId(e.target.value ? Number(e.target.value) : null)}
                  disabled={machineGroups.length === 0}
                >
                  <option value="">
                    {resolvedOperatorId > 0 && machineGroups.length === 0
                      ? 'No groups found'
                      : 'Select operator to load groups'}
                  </option>
                  {machineGroups.map(g => (
                    <option key={g.machineGroupId} value={g.machineGroupId}>
                      {g.machineGroupName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          <div className="pt-2 flex justify-end">
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Create Machine
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
